import type { ApiNode, ApiOnboardingGraph, Branch, VaultKind } from '@onefootprint/request-types/dashboard';
import { getSuccessors } from '../../hooks/use-node-context';
import {
  BO_KYC_NODE_Z_INDEX,
  EDGE_LABEL_AND_NODE_Z_INDEX,
  EDGE_Z_INDEX,
  nodeMeasurements,
} from '../../playbook-builder.constants';
import { type NodeTypesKey, reactFlowNodeType } from '../../playbook-builder.types';
import type { EdgeMetadata, InitialEdgeData, InitialNodeData } from './parse-graph.types';

export type EdgesFromData = {
  rootEdges: InitialEdgeData[];
  boKycEdges: InitialEdgeData[];
};

export type NodesFromData = {
  rootNodes: InitialNodeData[];
  boKycChildNodes: InitialNodeData[];
};

const nodesForNode = (node: ApiNode, graphId: string, subject: VaultKind): InitialNodeData[] => {
  const nodes: InitialNodeData[] = [createBaseNode(node, graphId, subject)];

  if (node.kind === 'conditional' || node.kind === 'branch') {
    // When we render the graph, we insert a node for the label of branch. This allows our
    // layouting logic to properly allocate horizontal space for even empty branches
    const branches = branchesForNode(node);
    const edgeLabelNodes = branches.map((b, index) =>
      createEdgeLabelNode(node, { branch: b, index, graphId, subject }),
    );
    nodes.push(...edgeLabelNodes);
  }

  return nodes;
};

const createBaseNode = (node: ApiNode, graphId: string, subject: VaultKind): InitialNodeData => {
  const kind: NodeTypesKey =
    node.kind === 'external_api' && typeof node.source === 'object' && 'vendorIntegration' in node.source
      ? 'vendor_integration'
      : node.kind;
  return {
    type: reactFlowNodeType(kind),
    id: node.id,
    data: { ...node, subject, graphId },
    position: { x: 0, y: 0 },
    // This will be overwritten for bo KYC nodes
    width: nodeMeasurements[kind].width,
    height: nodeMeasurements[kind].height,
    selectable: node.kind !== 'bo_kyc',
    className: `node_${kind}`,
    focusable: true,
    resizing: false,
    // Display buttons for add operations on top of edges as if they were edge labels
    zIndex: node.kind === 'bo_kyc' ? BO_KYC_NODE_Z_INDEX : EDGE_LABEL_AND_NODE_Z_INDEX,
  };
};

const createEdgeLabelNode = (
  node: ApiNode,
  { branch, index, graphId, subject }: { branch: Branch; index: number; graphId: string; subject: VaultKind },
): InitialNodeData => ({
  type: reactFlowNodeType('edge_label'),
  id: edgeLabelId(node, index),
  data: { label: branch.name, subject, graphId },
  position: { x: 0, y: 0 },
  width: nodeMeasurements.edge_label.width,
  height: nodeMeasurements.edge_label.height,
  selectable: false,
  className: `node_edge_label`,
  focusable: false,
  resizing: false,
  // Display buttons for add operations on top of edges as if they were edge labels
  zIndex: EDGE_LABEL_AND_NODE_Z_INDEX,
});

const edgeLabelId = (node: ApiNode, index: number) => `${node.id}-edge-label-${index}`;

export const branchesForNode = (node: ApiNode): Branch[] => {
  if (node.kind === 'conditional') {
    return [...node.conditionalBranches.map(b => b.branch), node.defaultBranch];
  }
  if (node.kind === 'branch') {
    return [...node.cases.map(b => b.branch), node.defaultBranch];
  }
  return [];
};

const edgesForNode = (node: ApiNode): InitialEdgeData[] => {
  if (node.kind === 'conditional' || node.kind === 'branch') {
    // When we render the graph, we insert a node for the label of branch. This allows our
    // layouting logic to properly allocate horizontal space for even empty branches
    const branches = branchesForNode(node);
    return branches.flatMap(({ successor }, index) => {
      const edgeLabelNodeId = edgeLabelId(node, index);
      const metadata = { sourceId: node.id, targetId: successor };
      return [
        createEdge(
          { source: node.id, target: edgeLabelNodeId },
          {
            ...metadata,
            isTargetLabel: true,
            edgeIndex: index,
          },
        ),
        createEdge({ source: edgeLabelNodeId, target: successor }, metadata),
      ];
    });
  }

  if (node.successor) {
    return [createEdge({ source: node.id, target: node.successor })];
  }

  return [];
};

// Edge `minlen`s are not set here — `recalculateJoinPointMinlens` computes them from the visible
// edge list so branches into a join point stay balanced as branches are collapsed.
const createEdge = (
  { source, target }: { source: string; target: string },
  { sourceId, targetId, isTargetLabel, edgeIndex }: Partial<EdgeMetadata> = {},
): InitialEdgeData => ({
  id: `${source}-${target}`,
  source,
  target,
  type: 'edge',
  zIndex: EDGE_Z_INDEX,
  data: {
    sourceId: sourceId ?? source,
    targetId: targetId ?? target,
    isTargetLabel: isTargetLabel ?? false,
    edgeIndex: edgeIndex ?? 0,
  },
});

type ParsedGraph = {
  nodes: NodesFromData;
  edges: EdgesFromData;
};

/** Recursively computes the graph, returning the nodes and edges for the provided node and its children. */
const computeSubgraph = (graph: ApiOnboardingGraph, node: ApiNode | undefined, visited: string[] = []) => {
  // NOTE: Probably no need to do this recursively anymore
  if (!node || visited.includes(node.id)) {
    return { nodes: [], edges: [] };
  }

  const subject = node.subgraphId ? 'person' : graph.subject;
  const nodes = nodesForNode(node, graph.id, subject);
  const edges = edgesForNode(node);

  let newVisited = [...visited, node.id];
  for (const successor of getSuccessors(node)) {
    const child = graph?.nodes.find(n => n.id === successor);
    const { nodes: successorNodes, edges: successorEdges } = computeSubgraph(graph, child, newVisited);
    newVisited = [...newVisited, ...successorNodes.map(n => n.id)];
    nodes.push(...successorNodes);
    edges.push(...successorEdges);
  }

  return { nodes, edges };
};

export const parseGraph = (graph?: ApiOnboardingGraph): ParsedGraph => {
  if (!graph) {
    return { nodes: { rootNodes: [], boKycChildNodes: [] }, edges: { rootEdges: [], boKycEdges: [] } };
  }

  const rootNode = graph?.nodes
    ?.filter(node => !node.subgraphId)
    .find(node => !graph?.nodes.some(n => getSuccessors(n).includes(node.id)));

  const boKycRootNode = graph?.nodes
    ?.filter(node => node.subgraphId)
    .find(node => !graph?.nodes.some(n => getSuccessors(n).includes(node.id)));

  const { nodes: rootNodes, edges: rootEdges } = computeSubgraph(graph, rootNode);
  const { nodes: boKycChildNodes, edges: boKycEdges } = computeSubgraph(graph, boKycRootNode);

  return { nodes: { rootNodes, boKycChildNodes }, edges: { rootEdges, boKycEdges } };
};
